import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Gap } from 'ui-components/components/flex';
import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { noop } from 'ui-components/default-props/noop';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import {
  NotificationService,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
} from 'ui-components/services/notification-service';
import { FIELD_NAMES } from '../../../../../../address/address-fields/constants';
import { ControlledComponentAddressForm } from '../../../../../../address/controlled-component';
import { SimpleModal } from '../../../../../../components/simple-modal';
import { supplierApi } from '../../../api/supplier-api';
import SupplierInputField from './supplier-input-field';
import { SUPPLIER_FIELD_NAMES } from './supplier-modal.constants';
import messages from './supplier-modal.messages';
import style from './supplier-modal.styles.module.less';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * All supplier fields
 * @type {Array<keyof Supplier>}
 */
const SUPPLIER_FIELDS = [
  'supplierName',
  'supplierEmail',
  'countryCode',
  'addressLine1',
  'addressLine2',
  'city',
  'state',
  'postalCode',
  'contactName',
  'contactPhone',
];

/**
 * Required supplier fields
 * @type {Array<keyof Supplier>}
 */
const REQUIRED_FIELDS = SUPPLIER_FIELDS.filter((field) => ['supplierName'].includes(field));

/**
 * Initial state for supplier
 * @type {Supplier}
 */
const initialSupplierState = Object.fromEntries(SUPPLIER_FIELDS.map((field) => [field, '']));

const ADDRESS_FIELDS = SUPPLIER_FIELDS.filter((field) =>
  ['addressLine1', 'addressLine2', 'city', 'state', 'postalCode', 'countryCode'].includes(field),
);

const { NAME, COMPANY, PHONE, EMAIL } = FIELD_NAMES;

/**
 * @typedef {object} Supplier
 * @property {number} [supplierId] - The unique identifier of the supplier
 * @property {string} [supplierName] - The name of the supplier
 * @property {string} [supplierEmail] - The email address of the supplier
 * @property {string} [countryCode] - The country code of the supplier's address
 * @property {string} [addressLine1] - The first line of the supplier's address
 * @property {string} [addressLine2] - The second line of the supplier's address
 * @property {string} [city] - The city of the supplier's address
 * @property {string} [state] - The state/province of the supplier's address
 * @property {string} [postalCode] - The postal code of the supplier's address
 * @property {string} [contactName] - The name of the supplier's contact person
 * @property {string} [contactPhone] - The phone number of the supplier's contact person
 */

/**
 * Modal component for creating or editing a supplier
 * @param {object} props - Component props
 * @param {() => void} props.onClose - Function to call when the modal should close
 * @param {() => void} [props.onSupplierSaved] - Function to call when a supplier is successfully saved
 * @param {Supplier} [props.supplier] - The supplier data to edit (if in edit mode)
 * @param {boolean} [props.isEditing] - Whether the modal is in edit mode
 * @param {boolean} [props.isLoading] - Whether the modal is in a loading state
 * @returns {JSX.Element} The rendered modal component
 */
export const SupplierModal = ({
  onClose,
  onSupplierSaved = noop,
  supplier: initialSupplier,
  isEditing = false,
  isLoading: externalLoading = false,
}) => {
  const [supplier, setSupplier] = useState(() => initialSupplier || initialSupplierState);
  useEffect(() => {
    if (initialSupplier) {
      setSupplier(initialSupplier);
    }
  }, [initialSupplier]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hiddenFieldsForAddressForm = [NAME, COMPANY, PHONE, EMAIL];
  const fieldMap = {
    [FIELD_NAMES.LINE1]: 'addressLine1',
    [FIELD_NAMES.LINE2]: 'addressLine2',
    [FIELD_NAMES.CITY]: 'city',
    [FIELD_NAMES.STATE]: 'state',
    [FIELD_NAMES.POSTAL_CODE]: 'postalCode',
    [FIELD_NAMES.COUNTRY_CODE]: 'countryCode',
  };

  const isFormValid = () => !REQUIRED_FIELDS.some((field) => errors[field] || !supplier[field]);

  const hasValidationErrors = () => Object.values(errors).some((error) => error);

  const hasChanges = useCallback(() => {
    if (!isEditing || !initialSupplier) return true;
    return SUPPLIER_FIELDS.some((field) => {
      const currentValue = supplier[field] || '';
      const initialValue = initialSupplier[field] || '';
      return currentValue !== initialValue;
    });
  }, [supplier, initialSupplier, isEditing]);

  const validateField = useCallback((fieldName, value) => {
    let hasError = false;
    switch (fieldName) {
      case 'supplierName':
        hasError = !value;
        break;
      case 'supplierEmail':
        hasError = value && !emailRegex.test(value);
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [fieldName]: hasError }));
    return hasError;
  }, []);

  const handleFieldBlur = useCallback(
    (fieldName) => {
      if (!ADDRESS_FIELDS.includes(fieldName)) {
        validateField(fieldName, supplier[fieldName]);
      }
    },
    [supplier, validateField],
  );

  const handleFieldChange = useCallback((field, value) => {
    setSupplier((prev) => ({
      ...prev,
      [field]: value || '',
    }));
  }, []);
  const showLoading = externalLoading || (isEditing && !initialSupplier);
  const [isSaving, setIsSaving] = useState(false);
  const handleSave = useCallback(async () => {
    const hasErrors = REQUIRED_FIELDS.some((field) => validateField(field, supplier[field]));
    if (hasErrors) return;

    setIsSubmitting(true);
    try {
      setIsSaving(true);
      const supplierPayload = {
        supplierName: supplier.supplierName,
        supplierEmail: supplier.supplierEmail,
        ...Object.fromEntries(ADDRESS_FIELDS.map((field) => [field, supplier[field] || ''])),
        contactName: supplier.contactName || '',
        contactPhone: supplier.contactPhone || '',
      };

      if (isEditing) {
        await supplierApi.updateSupplier(initialSupplier.supplierId, supplierPayload);
        NotificationService.showNotification({
          title: <FormattedMessage {...messages.supplierUpdatedSuccessTitle} />,
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          content: <FormattedMessage {...messages.supplierUpdatedSuccess} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
        onSupplierSaved();
      } else {
        await supplierApi.createSupplier(supplierPayload);
        NotificationService.showNotification({
          title: <FormattedMessage {...messages.supplierCreatedSuccessTitle} />,
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          content: <FormattedMessage {...messages.supplierCreatedSuccess} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
        onSupplierSaved();
      }
      onClose();
    } catch {
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.supplierSaveErrorTitle} />,
        notificationType: NOTIFICATION_TYPE_ERROR,
        content: <FormattedMessage {...messages.supplierSaveErrorContent} />,
        duration: NOTIFICATION_DURATIONS.SHORT,
      });
    } finally {
      setIsSaving(false);
      setIsSubmitting(false);
    }
  }, [supplier, validateField, onClose, isEditing, initialSupplier, onSupplierSaved]);

  return (
    <SimpleModal
      actionButtonTheme={THEME_CONSTANTS.primary}
      actionMessage={isEditing ? messages.save : messages.add}
      cancelMessage={messages.cancel}
      className={style['modal-supplier']}
      headerMessage={isEditing ? messages.editSupplierModalTitle : messages.supplierModalTitle}
      isActionDisabled={isSubmitting || !isFormValid() || hasValidationErrors() || (isEditing && !hasChanges())}
      onAction={handleSave}
      onCancel={onClose}
      onClose={onClose}
      size='custom'
    >
      {showLoading || isSaving ? (
        <LoadingIndicator className={style['modal-supplier-loading']} theme={THEME_CONSTANTS.grey} />
      ) : (
        <FlexColumn>
          <FlexRow className={style['flex-row']} gap={Gap.Space24}>
            <FlexColumn className={style['flex-column']}>
              <SupplierInputField
                fieldName={SUPPLIER_FIELD_NAMES.SUPPLIER_NAME}
                fieldValue={supplier.supplierName}
                hasError={errors.supplierName}
                label={messages.supplierName}
                onBlur={handleFieldBlur}
                onChange={handleFieldChange}
                required
              />
            </FlexColumn>
            <FlexColumn className={style['flex-column']}>
              <SupplierInputField
                fieldName={SUPPLIER_FIELD_NAMES.SUPPLIER_EMAIL}
                fieldValue={supplier.supplierEmail}
                hasError={errors.supplierEmail}
                label={messages.supplierEmail}
                onBlur={handleFieldBlur}
                onChange={handleFieldChange}
              />
            </FlexColumn>
          </FlexRow>

          <ControlledComponentAddressForm
            address={{
              line1: supplier.addressLine1 || '',
              line2: supplier.addressLine2 || '',
              city: supplier.city || '',
              state: supplier.state || '',
              postalCode: supplier.postalCode || '',
              countryCode: supplier.countryCode || '',
            }}
            allowEmptyValueToSelect
            hasError={errors}
            hiddenFields={hiddenFieldsForAddressForm}
            hidePasteAddress
            onBlur={(field, value) => {
              handleFieldBlur(fieldMap[field], value);
            }}
            onChange={(field, value) => {
              handleFieldChange(fieldMap[field], value);
            }}
            onCustomerSelected={noop}
            pasteAddressRequest={noop}
            requiredFields={REQUIRED_FIELDS}
            showAddressLine3={false}
          />

          <FlexRow className={style['flex-row']} gap={Gap.Space24}>
            <FlexColumn className={style['flex-column']}>
              <SupplierInputField
                fieldName={SUPPLIER_FIELD_NAMES.CONTACT_NAME}
                fieldValue={supplier.contactName}
                hasError={errors.contactName}
                label={messages.supplierContactName}
                onBlur={handleFieldBlur}
                onChange={handleFieldChange}
              />
            </FlexColumn>
            <FlexColumn className={style['flex-column']}>
              <SupplierInputField
                fieldName={SUPPLIER_FIELD_NAMES.CONTACT_PHONE}
                fieldValue={supplier.contactPhone}
                hasError={errors.contactPhone}
                label={messages.supplierContactPhone}
                onBlur={handleFieldBlur}
                onChange={handleFieldChange}
              />
            </FlexColumn>
          </FlexRow>
        </FlexColumn>
      )}
    </SimpleModal>
  );
};
